/**
 * DesignGapセクション — 構造の変化
 *
 * ネイタル (本来の構造) と カレント (今の構造) の比較。
 * 色は theme.colors.gradGold (#E6C77E→#F4B860) を使い、暗い背景でも読める。
 */

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { GitCompare, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Section = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: 32px;
  box-shadow: ${props => props.theme.shadows.lg};
  margin-bottom: 24px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.display};
  font-style: italic;
  color: ${props => props.theme.colors.text};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionDescription = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.85;
  margin-bottom: 24px;

  strong {
    color: ${props => props.theme.colors.text};
    font-weight: 500;
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StructureCard = styled.div`
  background: ${props => props.theme.colors.bgPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CardLabel = styled.div`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 1.5px;
  font-family: ${props => props.theme.fonts.mono};
`;

const CardCaption = styled.div`
  font-size: 0.72rem;
  color: ${props => props.theme.colors.textMuted};
  margin-top: -2px;
  line-height: 1.6;
`;

const TypeCode = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  font-family: ${props => props.theme.fonts.mono};
  color: ${props => props.theme.colors.primary};
  letter-spacing: 0.04em;
  line-height: 1.1;
`;

const TypeName = styled.div`
  font-size: 1.05rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 4px;
`;

const TypeDescription = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.75;
  margin-top: 4px;
`;

const ArrowSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 4px 0;
    flex-direction: row;
    gap: 10px;
  }
`;

const Arrow = styled.div`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  line-height: 1;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    transform: rotate(90deg);
  }
`;

const ArrowCaption = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.textMuted};
  margin-top: 6px;
  letter-spacing: 0.05em;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-top: 0;
  }
`;

const GapList = styled.div`
  margin-top: 28px;
`;

const GapListTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  margin-bottom: 6px;
`;

const GapListSubtitle = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.textMuted};
  margin-bottom: 14px;
  line-height: 1.7;
`;

const GapItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${props => props.$background || 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.$borderColor || props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  margin-bottom: 8px;
  gap: 12px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 10px 12px;
  }
`;

const GapAxis = styled.div`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  font-size: 0.92rem;
`;

const GapValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: ${props => props.$color || props.theme.colors.text};
  font-size: 0.92rem;

  .label {
    font-weight: 400;
    font-size: 0.78rem;
    color: ${props => props.theme.colors.textSecondary};
  }

  .delta {
    font-family: ${props => props.theme.fonts.mono};
    font-size: 0.85rem;
  }
`;

function DesignGapSection({ natal, current, designGap }) {
  const { t } = useLanguage();

  if (!natal || !current) return null;

  const AXIS_LABELS = {
    '起動軸': t('行動特性', 'Behavioral'),
    '判断軸': t('判断基準', 'Decision'),
    '選択軸': t('価値観', 'Values'),
    '共鳴軸': t('共感性', 'Empathy'),
    '自覚軸': t('自己認識', 'Self-Awareness')
  };

  // 軸ごとに「どちらに動いた」の自然言語ラベル
  const AXIS_POLES = {
    '起動軸': { plus: '自発寄りへ', minus: '応答寄りへ' },
    '判断軸': { plus: '論理寄りへ', minus: '直感寄りへ' },
    '選択軸': { plus: '理想寄りへ', minus: '実利寄りへ' },
    '共鳴軸': { plus: '共感寄りへ', minus: '自律寄りへ' },
    '自覚軸': { plus: '内省寄りへ', minus: '実践寄りへ' },
  };

  // theme colors より直接参照したい色 (gold / muted)
  // 構造変化を「強い揺らぎ」「ゆるい揺らぎ」「ほぼ動かず」で表現
  const getGapDescriptor = (value, axis) => {
    const poles = AXIS_POLES[axis] || { plus: '', minus: '' };
    const dir = value > 0 ? poles.plus : poles.minus;
    const mag = Math.abs(value);
    if (mag > 0.15) {
      return {
        Icon: value > 0 ? TrendingUp : TrendingDown,
        directionLabel: dir,
        magnitudeLabel: t('大きく揺らいでいます', 'Major shift'),
        accent: '#E6C77E',          // 金 — 注目すべき変化
        background: 'rgba(230, 199, 126, 0.06)',
        borderColor: 'rgba(230, 199, 126, 0.30)',
      };
    }
    if (mag > 0.05) {
      return {
        Icon: value > 0 ? TrendingUp : TrendingDown,
        directionLabel: dir,
        magnitudeLabel: t('やや動いています', 'Slight shift'),
        accent: '#C4C8D8',          // 淡銀 — 中程度
        background: 'rgba(196, 200, 216, 0.04)',
        borderColor: 'rgba(196, 200, 216, 0.20)',
      };
    }
    return {
      Icon: Minus,
      directionLabel: t('ほぼそのまま', 'Mostly unchanged'),
      magnitudeLabel: '',
      accent: '#8A93B0',
      background: 'rgba(255, 255, 255, 0.02)',
      borderColor: 'rgba(255, 255, 255, 0.06)',
    };
  };

  return (
    <Section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <SectionTitle>
        <GitCompare size={22} />
        {t('構造の変化', 'Structural Changes')}
      </SectionTitle>

      <SectionDescription>
        {t(
          'STRUCT CODE は、',
          'STRUCT CODE compares '
        )}
        <strong>{t('ネイタル (本来の構造)', 'Natal (your innate structure)')}</strong>
        {t(
          ' と ',
          ' with '
        )}
        <strong>{t('カレント (今の構造)', 'Current (your present structure)')}</strong>
        {t(
          ' の二つを並べて読み解きます。生まれ持った構造に対して、今のあなたがどの方向にどれくらい動いてきたか — それが下の各軸の差分です。',
          '. The difference between them shows which axis has shifted, and how far.'
        )}
      </SectionDescription>

      <ComparisonGrid>
        <StructureCard>
          <CardLabel>{t('ネイタル', 'Natal')}</CardLabel>
          <CardCaption>{t('生まれ持った本来の構造', 'Your innate structure')}</CardCaption>
          <TypeCode>{natal.type}</TypeCode>
          <TypeName>{natal.type_name}</TypeName>
          {natal.description && (
            <TypeDescription>{natal.description}</TypeDescription>
          )}
        </StructureCard>

        <ArrowSection>
          <Arrow>→</Arrow>
          <ArrowCaption>{t('時期の影響', 'Temporal shift')}</ArrowCaption>
        </ArrowSection>

        <StructureCard>
          <CardLabel>{t('カレント', 'Current')}</CardLabel>
          <CardCaption>{t('今この時期の構造', 'Your structure today')}</CardCaption>
          <TypeCode>{current.type}</TypeCode>
          <TypeName>{current.type_name}</TypeName>
          {current.description && (
            <TypeDescription>{current.description}</TypeDescription>
          )}
        </StructureCard>
      </ComparisonGrid>

      {designGap && (
        <GapList>
          <GapListTitle>{t('各軸の差分', 'Axis-by-axis shift')}</GapListTitle>
          <GapListSubtitle>
            {t(
              'ネイタルから、カレントへ。どの軸が、どちらの方向へ動いたか。',
              'Direction and magnitude of each axis shift, from natal to current.'
            )}
          </GapListSubtitle>
          {Object.entries(designGap).map(([axis, value]) => {
            const d = getGapDescriptor(value, axis);
            const { Icon } = d;
            const sign = value > 0 ? '+' : '';
            return (
              <GapItem key={axis} $background={d.background} $borderColor={d.borderColor}>
                <GapAxis>{AXIS_LABELS[axis] || axis}</GapAxis>
                <GapValue $color={d.accent}>
                  <Icon size={15} />
                  <span>{d.directionLabel}</span>
                  {d.magnitudeLabel && (
                    <span className="label">／ {d.magnitudeLabel}</span>
                  )}
                  <span className="delta">{sign}{(value * 100).toFixed(0)}%</span>
                </GapValue>
              </GapItem>
            );
          })}
        </GapList>
      )}
    </Section>
  );
}

export default DesignGapSection;
